/**
 * Right panel: config for selected activity (common fields + handler-specific).
 */

import { Input } from "../../primitives/Input";
import { Button } from "../../primitives/Button";
import type { ProcessActivityTemplateStudioOut } from "../../../api/opsV2/types";

type ActivityConfigPanelProps = {
  activity: ProcessActivityTemplateStudioOut | null;
  onActivityChange: (activity: ProcessActivityTemplateStudioOut) => void;
  templateId: string;
};

const ASSIGNEE_POLICY_OPTIONS = [
  { value: "manual", label: "Manuel (choisi à l'exécution)" },
  { value: "role_based", label: "Par rôle" },
];

const ROLES_CLOSED = [
  { value: "responsable_integration", label: "Responsable intégration" },
  { value: "directeur_ops", label: "Directeur ops" },
];

export function ActivityConfigPanel({ activity, onActivityChange, templateId }: ActivityConfigPanelProps) {
  if (!activity) {
    return (
      <div className="text-sm text-muted py-4">
        Sélectionnez une activité au centre pour configurer titre, type, SLA et options du handler.
      </div>
    );
  }

  const update = (patch: Partial<ProcessActivityTemplateStudioOut>) => {
    onActivityChange({ ...activity, ...patch });
  };

  const uiConfig = activity.activity_ui_config ?? {};
  const setUiConfig = (key: string, value: unknown) => {
    update({ activity_ui_config: { ...uiConfig, [key]: value } });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-text">Configuration de l'activité</h3>

      <div className="space-y-3">
        <Input
          label="Titre"
          value={activity.title}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="Titre affiché"
        />
        <Input
          label="Description"
          value={activity.description ?? ""}
          onChange={(e) => update({ description: e.target.value || null })}
          placeholder="Optionnel"
        />
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="required"
            checked={activity.is_required}
            onChange={(e) => update({ is_required: e.target.checked })}
          />
          <label htmlFor="required" className="text-sm">Requis</label>
        </div>
        <Input
          label="SLA (heures)"
          value={activity.sla_hours != null ? String(activity.sla_hours) : ""}
          onChange={(e) => update({ sla_hours: e.target.value === "" ? null : parseInt(e.target.value, 10) || null })}
          placeholder="Optionnel"
        />
        <div>
          <label className="block text-sm font-medium text-text mb-1">Assignation (V1)</label>
          <select
            className="w-full border border-border rounded px-2 py-1.5 text-sm"
            value={activity.assignee_type ?? "manual"}
            onChange={(e) => update({ assignee_type: e.target.value })}
          >
            {ASSIGNEE_POLICY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {(activity.assignee_type === "role_based") && (
            <select
              className="w-full mt-1 border border-border rounded px-2 py-1.5 text-sm"
              value={activity.assignee_role ?? ""}
              onChange={(e) => update({ assignee_role: e.target.value || null })}
            >
              <option value="">Sélectionner un rôle</option>
              {ROLES_CLOSED.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Handler-specific config */}
      <div className="border-t border-border pt-4">
        <h4 className="text-sm font-medium text-text mb-2">Config handler: {activity.activity_type}</h4>
        {activity.activity_type === "select_plan" && (
          <div className="space-y-2 text-sm">
            <p className="text-muted">Options: forfait_1, forfait_2. Clé contexte: drone_academie_plan.</p>
            <Input
              label="Plan par défaut"
              value={(uiConfig.default_plan as string) ?? ""}
              onChange={(e) => setUiConfig("default_plan", e.target.value || undefined)}
              placeholder="forfait_1 | forfait_2"
            />
          </div>
        )}
        {activity.activity_type === "send_email" && (
          <div className="space-y-2 text-sm">
            <Input
              label="template_code (requis à la publication)"
              value={(uiConfig.template_code as string) ?? ""}
              onChange={(e) => setUiConfig("template_code", e.target.value)}
              placeholder="ex. da_activation_forfait_1"
            />
            <div>
              <label className="block text-sm font-medium text-text mb-1">Variables (plan, client, assignee…)</label>
              <textarea
                className="w-full border border-border rounded px-2 py-1.5 text-sm min-h-[60px]"
                value={typeof uiConfig.variables === "object" && uiConfig.variables !== null ? JSON.stringify((uiConfig as { variables?: Record<string, string> }).variables, null, 2) : ""}
                onChange={(e) => {
                  try {
                    const v = e.target.value.trim() ? JSON.parse(e.target.value) : {};
                    setUiConfig("variables", typeof v === "object" && v !== null ? v : {});
                  } catch {
                    setUiConfig("variables", {});
                  }
                }}
                placeholder='{"plan": "drone_academie_plan", "client": "client_id"}'
              />
            </div>
            <p className="text-muted">Mode: preview | send. Destinataires calculés backend (template-first).</p>
          </div>
        )}
        {activity.activity_type === "checklist_or_form" && (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="merge_to_context"
                checked={!!uiConfig.merge_to_context}
                onChange={(e) => setUiConfig("merge_to_context", e.target.checked)}
              />
              <label htmlFor="merge_to_context">Fusionner dans le contexte</label>
            </div>
            <Input
              label="next_followup_days (défaut)"
              value={uiConfig.defaults?.next_followup_days != null ? String((uiConfig.defaults as { next_followup_days?: number }).next_followup_days) : ""}
              onChange={(e) => setUiConfig("defaults", { ...((uiConfig.defaults as object) ?? {}), next_followup_days: e.target.value === "" ? undefined : parseInt(e.target.value, 10) })}
              placeholder="ex. 10"
            />
            <div>
              <label className="block text-sm font-medium text-text mb-1">Champs prédéfinis</label>
              <div className="flex flex-wrap gap-2">
                {["notes", "satisfaction", "accept_refuse"].map((f) => (
                  <label key={f} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={((uiConfig.fields as string[]) ?? []).includes(f)}
                      onChange={(e) => {
                        const cur = (uiConfig.fields as string[]) ?? [];
                        const next = e.target.checked ? [...cur, f] : cur.filter((x) => x !== f);
                        setUiConfig("fields", next);
                      }}
                    />
                    <span className="text-sm">{f}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Items checklist (label, required)</label>
              <ul className="space-y-1">
                {((uiConfig.items as { label: string; required?: boolean }[]) ?? []).map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      className="flex-1 border border-border rounded px-2 py-1 text-sm"
                      value={item.label}
                      onChange={(e) => {
                        const items = (uiConfig.items as { label: string; required?: boolean }[]) ?? [];
                        const next = [...items];
                        next[i] = { ...next[i], label: e.target.value };
                        setUiConfig("items", next);
                      }}
                      placeholder="Libellé"
                    />
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={!!item.required}
                        onChange={(e) => {
                          const items = (uiConfig.items as { label: string; required?: boolean }[]) ?? [];
                          const next = [...items];
                          next[i] = { ...next[i], required: e.target.checked };
                          setUiConfig("items", next);
                        }}
                      />
                      requis
                    </label>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline"
                    onClick={() => setUiConfig("items", [...((uiConfig.items as { label: string; required?: boolean }[]) ?? []), { label: "", required: true }])}
                  >
                    + Ajouter un item
                  </button>
                </li>
              </ul>
            </div>
          </div>
        )}
        {activity.activity_type === "ops_inputs" && (
          <div className="space-y-2 text-sm text-muted">
            <p>Champs: km, ha(ac), hours. Unité area: ha | ac. apply_to_products: all (V1).</p>
          </div>
        )}
        {!["select_plan", "send_email", "checklist_or_form", "ops_inputs"].includes(activity.activity_type) && (
          <p className="text-muted text-sm">Config manuelle ou non gérée pour ce type.</p>
        )}
      </div>
    </div>
  );
}
